import React, { useEffect, useState, useCallback } from "react";
import { getWarehouses, getPurchaseOrders, getPurchaseOrderById } from "../services/procurementApi";
import { allocateLocation, getItems, getUnallocatedItems, removeAllocation } from "../services/inventoryApi";
import "./ProcurementPage.css";

const PAGE_SIZE = 24;

const PLACEHOLDER_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' font-size='12' text-anchor='middle' fill='%2394a3b8' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

const WarehouseAllocationPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [lots, setLots] = useState([]); // flattened { id, label } across all POs — this is what allocate needs
  const [warehouseId, setWarehouseId] = useState("");
  const [lotId, setLotId] = useState("");
  const [sku, setSku] = useState("");
  const [isBoot, setIsBoot] = useState(false);
  const [isHighValue, setIsHighValue] = useState(false);
  const [results, setResults] = useState([]);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  // Allocated-articles gallery — every SKU currently on a shelf, with its
  // image and physical location, loaded a page at a time ("load more").
  const [articleTab, setArticleTab] = useState("allocated"); // "allocated" | "unallocated"
  const [articles, setArticles] = useState([]);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [articlesOffset, setArticlesOffset] = useState(0);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [removingSku, setRemovingSku] = useState(null);

  const loadArticles = useCallback(async (offset = 0, append = false, tab = articleTab) => {
    setArticlesLoading(true);
    try {
      const fetcher = tab === "unallocated" ? getUnallocatedItems : getItems;
      const { items, total } = await fetcher({ limit: PAGE_SIZE, offset });
      setArticles((prev) => (append ? [...prev, ...items] : items));
      setArticlesTotal(total);
      setArticlesOffset(offset + items.length);
    } catch (err) {
      // non-fatal — the allocate form above still works without this list
    } finally {
      setArticlesLoading(false);
    }
  }, [articleTab]);

  useEffect(() => {
    loadArticles(0, false, articleTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleTab]);

  const handleTabSwitch = (tab) => {
    setArticleTab(tab);
  };

  const handleRemoveAllocation = async (sku) => {
    if (!window.confirm(`Remove ${sku} from its bin? It'll free up the slot and move to Unallocated.`)) return;
    setRemovingSku(sku);
    try {
      await removeAllocation(sku);
      setMsg({ type: "success", text: `${sku} removed from its bin.` });
      loadArticles(0, false, "allocated");
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to remove allocation." });
    } finally {
      setRemovingSku(null);
    }
  };

  const handleQuickAllocate = (itemSku) => {
    setSku(itemSku);
    setArticleTab("allocated");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    (async () => {
      try {
        const [whs, pos] = await Promise.all([getWarehouses(), getPurchaseOrders()]);
        setWarehouses(whs);
        setWarehouseId(whs[0]?.id || "");

        // Purchase orders don't carry their lots in the list endpoint — fetch each
        // PO's detail to get the actual lot ids (allocate needs a LOT id, not a PO id).
        const details = await Promise.all(pos.map((po) => getPurchaseOrderById(po.id).catch(() => null)));
        const flatLots = [];
        details.forEach((po) => {
          if (!po || !Array.isArray(po.lots)) return;
          po.lots.forEach((lot) => {
            flatLots.push({ id: lot.id, label: `${po.po_number} — ${lot.lot_identifier}` });
          });
        });
        setLots(flatLots);
      } catch (err) {
        setMsg({ type: "error", text: "Failed to load warehouses / purchase orders." });
      }
    })();
  }, []);

  const handleAllocate = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!sku || !warehouseId) {
      setMsg({ type: "error", text: "Scan/enter a SKU and pick a warehouse first." });
      return;
    }
    setLoading(true);
    try {
      const res = await allocateLocation({
        sku: sku.trim(),
        warehouse_id: warehouseId,
        purchase_order_lot_id: lotId || undefined,
        is_boot: isBoot,
        is_high_value: isHighValue,
      });
      setResults((prev) => [res, ...prev]);
      setMsg({
        type: "success",
        text: res.already_allocated
          ? `${res.sku} was already at ${res.allocated_location.full_code}.`
          : `${res.sku} → ${res.allocated_location.full_code}`,
      });
      setSku("");
      setIsBoot(false);
      setIsHighValue(false);
      loadArticles(0, false, "allocated"); // refresh the gallery so the new bin shows up immediately
      setArticleTab("allocated");
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Allocation failed." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="procurement-page">
      <h1>Warehouse Allocation</h1>
      <p className="subtitle">
        Scan or type the SKU of an item you already created (Smart Scan / Add Product) to assign it a
        physical bin — first-fit, category-aware. This never creates a new SKU; it only places an
        existing one in the warehouse.
      </p>

      {msg && <div className={`proc-msg ${msg.type}`}>{msg.text}</div>}

      <div className="proc-section">
        <h2><span className="proc-step-badge">Allocate</span> Scan an item into a bin</h2>
        <form onSubmit={handleAllocate}>
          <div className="proc-grid" style={{ marginBottom: 14 }}>
            <div className="proc-field">
              <label>SKU (scan or type)</label>
              <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="JD1001" autoFocus />
            </div>
            <div className="proc-field">
              <label>Warehouse</label>
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">Select warehouse</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
              </select>
            </div>
            <div className="proc-field">
              <label>Purchase order lot (optional)</label>
              <select value={lotId} onChange={(e) => setLotId(e.target.value)}>
                <option value="">No PO</option>
                {lots.map((lot) => <option key={lot.id} value={lot.id}>{lot.label}</option>)}
              </select>
            </div>
            <div className="proc-field" style={{ alignSelf: "end" }}>
              <label><input type="checkbox" checked={isBoot} onChange={(e) => setIsBoot(e.target.checked)} style={{ width: "auto", marginRight: 6 }} />Boots (2 per cubby)</label>
            </div>
            <div className="proc-field" style={{ alignSelf: "end" }}>
              <label><input type="checkbox" checked={isHighValue} onChange={(e) => setIsHighValue(e.target.checked)} style={{ width: "auto", marginRight: 6 }} />High value (locked cabinet)</label>
            </div>
          </div>
          <button type="submit" className="proc-btn primary" disabled={loading}>{loading ? "Allocating…" : "Allocate Location"}</button>
        </form>
      </div>

      {results.length > 0 && (
        <div className="proc-section">
          <h2>Recently Allocated (this session)</h2>
          <table className="proc-table">
            <thead><tr><th>SKU</th><th>Title</th><th>Location</th></tr></thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i}>
                  <td className="proc-unit-cost">{r.sku}</td>
                  <td>{r.title || "—"}</td>
                  <td className="proc-unit-cost">{r.allocated_location.full_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="proc-section">
        <h2 style={{ justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {articleTab === "allocated" ? "Allocated Articles" : "Unallocated Items"}
            <span className="proc-count-pill">{articlesTotal}</span>
          </span>
          <span style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className="proc-btn secondary"
              style={{ padding: "6px 14px", fontSize: 13, fontWeight: articleTab === "allocated" ? 800 : 500, background: articleTab === "allocated" ? "#eff6ff" : undefined, borderColor: articleTab === "allocated" ? "#3b82f6" : undefined }}
              onClick={() => handleTabSwitch("allocated")}
            >
              Allocated
            </button>
            <button
              type="button"
              className="proc-btn secondary"
              style={{ padding: "6px 14px", fontSize: 13, fontWeight: articleTab === "unallocated" ? 800 : 500, background: articleTab === "unallocated" ? "#eff6ff" : undefined, borderColor: articleTab === "unallocated" ? "#3b82f6" : undefined }}
              onClick={() => handleTabSwitch("unallocated")}
            >
              Unallocated
            </button>
          </span>
        </h2>
        <p className="subtitle" style={{ marginBottom: 14 }}>
          {articleTab === "allocated"
            ? "Every SKU currently sitting on a shelf, with its photo and bin — newest first."
            : "Products that exist but haven't been assigned a physical bin yet. Quick-allocate from here."}
        </p>

        {articles.length === 0 && !articlesLoading && (
          <div className="pick-empty">
            {articleTab === "allocated" ? "Nothing allocated yet — scan an item above." : "Nothing unallocated — everything has a bin."}
          </div>
        )}

        <div className="article-grid">
          {articles.map((a) => (
            <div className="article-card" key={a.sku}>
              <img
                src={a.image_url || PLACEHOLDER_IMG}
                alt={a.title || a.sku}
                onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
              />
              <div className="article-card-body">
                <div className="article-sku">{a.sku}</div>
                <div className="article-title">{a.title || "Untitled"}</div>
                {articleTab === "allocated" ? (
                  <>
                    <div className="article-location">{a.full_code}</div>
                    <span className={`status-chip status-${(a.status || "stored").toLowerCase()}`}>
                      {a.status || "stored"}
                    </span>
                    <button
                      type="button"
                      className="proc-btn secondary"
                      style={{ marginTop: 8, padding: "5px 10px", fontSize: 12, color: "#ef4444", width: "100%" }}
                      disabled={removingSku === a.sku}
                      onClick={() => handleRemoveAllocation(a.sku)}
                    >
                      {removingSku === a.sku ? "Removing…" : "🗑 Remove"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="proc-btn primary"
                    style={{ marginTop: 8, padding: "5px 10px", fontSize: 12, width: "100%" }}
                    onClick={() => handleQuickAllocate(a.sku)}
                  >
                    📦 Allocate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {articlesOffset < articlesTotal && (
          <div style={{ textAlign: "center", marginTop: 18 }}>
            <button
              type="button"
              className="proc-btn secondary"
              disabled={articlesLoading}
              onClick={() => loadArticles(articlesOffset, true, articleTab)}
            >
              {articlesLoading ? "Loading…" : `Load more (${articlesTotal - articlesOffset} left)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseAllocationPage;
